import { z } from 'zod';

export const CROWDSTRIKE_CLOUD_REGIONS = [
  'us-1',
  'us-2',
  'eu-1',
  'us-gov-1',
] as const;
export const CrowdStrikeCloudRegionSchema = z.enum(CROWDSTRIKE_CLOUD_REGIONS);
export type CrowdStrikeCloudRegion = z.infer<
  typeof CrowdStrikeCloudRegionSchema
>;

export const CROWDSTRIKE_CRITICALITY_LEVELS = [
  'Critical',
  'High',
  'Noncritical',
  'Unassigned',
] as const;
export const CrowdStrikeCriticalitySchema = z.enum(
  CROWDSTRIKE_CRITICALITY_LEVELS,
);
export type CrowdStrikeCriticality = z.infer<
  typeof CrowdStrikeCriticalitySchema
>;

export const CrowdStrikeServiceSchema = z.object({
  port: z.number().nullable().optional(),
  transport: z.string().nullable().optional(),
  protocol: z.string().nullable().optional(),
  protocol_port: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  status_code: z.number().nullable().optional(),
  first_seen: z.string().nullable().optional(),
  last_seen: z.string().nullable().optional(),
  platform_name: z.string().nullable().optional(),
  cloud_provider: z.string().nullable().optional(),
  hosting_provider: z.string().nullable().optional(),
  cpes: z.array(z.string()).optional(),
  applications: z
    .array(
      z.object({
        name: z.string().nullable().optional(),
        vendor: z.string().nullable().optional(),
        version: z.string().nullable().optional(),
        category: z.string().nullable().optional(),
        cpe: z.string().nullable().optional(),
      }),
    )
    .optional(),
});
export type CrowdStrikeService = z.infer<typeof CrowdStrikeServiceSchema>;

export const CrowdStrikeCloudVmSchema = z.object({
  instance_id: z.string().nullable().optional(),
  platform: z.string().nullable().optional(),
  region: z.string().nullable().optional(),
  public_ip: z.string().nullable().optional(),
  private_ip: z.string().nullable().optional(),
  mac_address: z.string().nullable().optional(),
  owner_id: z.string().nullable().optional(),
  security_groups: z.array(z.string()).optional(),
  status: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
  lifecycle: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
});
export type CrowdStrikeCloudVm = z.infer<typeof CrowdStrikeCloudVmSchema>;

export const CrowdStrikeLocationSchema = z.object({
  country_name: z.string().nullable().optional(),
  country_code: z.string().nullable().optional(),
  region_name: z.string().nullable().optional(),
  region_code: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  postal_code: z.string().nullable().optional(),
  timezone: z.string().nullable().optional(),
  area_code: z.number().nullable().optional(),
});
export type CrowdStrikeLocation = z.infer<typeof CrowdStrikeLocationSchema>;

export const CrowdStrikeIpSchema = z.object({
  ip_address: z.string().nullable().optional(),
  fqdns: z.array(z.string()).optional(),
  isp: z.string().nullable().optional(),
  asn: z.number().nullable().optional(),
  ptr: z.string().nullable().optional(),
  aid: z.string().nullable().optional(),
  location: CrowdStrikeLocationSchema.nullable().optional(),
  cloud_vm: CrowdStrikeCloudVmSchema.nullable().optional(),
  services: z.array(CrowdStrikeServiceSchema).optional(),
});
export type CrowdStrikeIp = z.infer<typeof CrowdStrikeIpSchema>;

export const CrowdStrikeDnsDomainSchema = z.object({
  fqdn: z.string().nullable().optional(),
  parent_domain: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  resolved_ips: z.array(z.string()).optional(),
  isps: z.array(z.string()).optional(),
  services: z.array(CrowdStrikeServiceSchema).optional(),
});
export type CrowdStrikeDnsDomain = z.infer<typeof CrowdStrikeDnsDomainSchema>;

export const CrowdStrikeTriageSchema = z.object({
  status: z.string().nullable().optional(),
  action: z.string().nullable().optional(),
  assigned_to: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  updated_by: z.string().nullable().optional(),
  updated_timestamp: z.string().nullable().optional(),
});
export type CrowdStrikeTriage = z.infer<typeof CrowdStrikeTriageSchema>;

export const CrowdStrikeDiscoveryStepSchema = z.object({
  entity: z.string().nullable().optional(),
  entity_type: z.string().nullable().optional(),
  clue: z.string().nullable().optional(),
});

export const CrowdStrikeDiscoverySchema = z.object({
  path: z
    .object({
      steps: z.array(CrowdStrikeDiscoveryStepSchema).optional(),
    })
    .nullable()
    .optional(),
});

export const CrowdStrikeSubsidiarySchema = z.object({
  id: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
});

export const CrowdStrikeExternalAssetSchema = z.object({
  id: z.string(),
  asset_type: z.string().nullable().optional(),
  criticality: z.string().nullable().optional(),
  criticality_description: z.string().nullable().optional(),
  criticality_timestamp: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  connectivity_status: z.string().nullable().optional(),
  confidence: z.number().nullable().optional(),
  first_seen: z.string().nullable().optional(),
  last_seen: z.string().nullable().optional(),
  internet_exposure: z.string().nullable().optional(),
  perimeter: z.string().nullable().optional(),
  discovered_by: z.string().nullable().optional(),
  manual: z.boolean().nullable().optional(),
  ip: CrowdStrikeIpSchema.nullable().optional(),
  dns_domain: CrowdStrikeDnsDomainSchema.nullable().optional(),
  triage: CrowdStrikeTriageSchema.nullable().optional(),
  discovery: CrowdStrikeDiscoverySchema.nullable().optional(),
  subsidiaries: z.array(CrowdStrikeSubsidiarySchema).optional(),
  data_providers: z.array(z.string()).optional(),
});
export type CrowdStrikeExternalAsset = z.infer<
  typeof CrowdStrikeExternalAssetSchema
>;

export const CrowdStrikeConnectBodySchema = z.object({
  clientId: z.string().min(1),
  clientSecret: z.string().min(1),
  cloud: CrowdStrikeCloudRegionSchema,
  criticalityFilter: z.array(CrowdStrikeCriticalitySchema).optional(),
  pollIntervalMinutes: z.int().min(1).max(60).optional(),
  firstFetchLookbackDays: z.int().min(1).max(365).optional(),
});
export type CrowdStrikeConnectBody = z.infer<
  typeof CrowdStrikeConnectBodySchema
>;

export const CrowdStrikeSyncStateSchema = z.enum([
  'CONNECTING',
  'BACKFILLING',
  'LISTENING',
  'ERROR',
]);
export type CrowdStrikeSyncState = z.infer<typeof CrowdStrikeSyncStateSchema>;

export const CrowdStrikeConnectionStatusResponseSchema = z.object({
  provider: z.literal('crowdstrike'),
  status: z.enum(['VALID', 'EXPIRED', 'REVOKED', 'NOT_CONNECTED']),
  cloud: CrowdStrikeCloudRegionSchema.nullable(),
  syncState: CrowdStrikeSyncStateSchema.nullable(),
  pollIntervalMinutes: z.int().nullable(),
  lastSyncTimestamp: z.string().nullable(),
  lastSyncAt: z.string().nullable(),
  lastError: z.string().nullable(),
});
export type CrowdStrikeConnectionStatusResponse = z.infer<
  typeof CrowdStrikeConnectionStatusResponseSchema
>;
